import { CRIMINAL_CATEGORIES } from './criminalProceedings';
import {
  flattenCriminalProceedingsForRegistry,
  findCriminalProceedingById,
  getCriminalProceeding,
  getSelectableProceedingsForCategory,
  LEGACY_CRIMINAL_SUBTYPE_MAP,
  LEGACY_CRIMINAL_TYPE_MAP,
  type CriminalTaxonomyConfig,
} from './criminalTaxonomyHelpers';

export * from './criminalTaxonomyHelpers';
export { CRIMINAL_CATEGORIES } from './criminalProceedings';
export { SPECIAL_COURT_SUBTYPES } from './criminalTaxonomyHelpers';

export const CRIMINAL_CASE_TAXONOMY_CONFIG: CriminalTaxonomyConfig = {
  id: 'criminal',
  label: 'Criminal',
  description:
    'Criminal trials, bail matters, appeals, revisions, complaints, quashment, and related criminal proceedings.',
  active: true,
  categories: CRIMINAL_CATEGORIES,
  types: flattenCriminalProceedingsForRegistry(CRIMINAL_CATEGORIES),
};

export const getCriminalCaseTypes = () => CRIMINAL_CASE_TAXONOMY_CONFIG.categories;

export const getCriminalCategories = () => CRIMINAL_CASE_TAXONOMY_CONFIG.categories;

export const getCriminalSubtypes = (caseTypeId: string) => {
  const proceeding = findCriminalProceedingById(CRIMINAL_CASE_TAXONOMY_CONFIG, caseTypeId);
  return proceeding?.subtypes ?? [];
};

export const getCriminalProceedings = (categoryId: string) =>
  getSelectableProceedingsForCategory(CRIMINAL_CASE_TAXONOMY_CONFIG, categoryId);

export const normalizeCriminalCaseClassification = (existingCase: Record<string, unknown>) => {
  if (existingCase.caseCategory === 'Criminal' && existingCase.criminalCategory && existingCase.proceedingType) {
    return existingCase;
  }

  const legacyType = String(
    existingCase.matterType || existingCase.caseTypeName || existingCase.caseType || '',
  );
  const lowerType = legacyType.toLowerCase();
  const legacySubtype = String(existingCase.caseSubtype || '').toLowerCase();

  let caseCategory = existingCase.caseCategory;
  let criminalCategory = existingCase.criminalCategory as string | undefined;
  let caseType = legacyType;
  let proceedingType = existingCase.proceedingType as string | undefined;
  let caseSubtype = existingCase.caseSubtype as string | undefined;

  const subtypeMatch = LEGACY_CRIMINAL_SUBTYPE_MAP[legacySubtype] || LEGACY_CRIMINAL_SUBTYPE_MAP[lowerType];
  const typeMatch = LEGACY_CRIMINAL_TYPE_MAP[legacyType] || LEGACY_CRIMINAL_TYPE_MAP[lowerType];

  if (subtypeMatch) {
    caseCategory = 'Criminal';
    criminalCategory = subtypeMatch.categoryId;
    proceedingType = subtypeMatch.proceedingId;
    caseType = subtypeMatch.proceedingId;
  } else if (typeMatch) {
    caseCategory = 'Criminal';
    criminalCategory = typeMatch.categoryId;
    proceedingType = typeMatch.proceedingId;
    caseType = typeMatch.proceedingId;
  } else {
    const direct = findCriminalProceedingById(CRIMINAL_CASE_TAXONOMY_CONFIG, legacyType);
    if (direct) {
      caseCategory = 'Criminal';
      criminalCategory = direct.criminalCategoryId;
      proceedingType = direct.id;
      caseType = direct.id;
    } else if (CRIMINAL_CASE_TAXONOMY_CONFIG.categories.some((c) => c.id === legacyType)) {
      caseCategory = 'Criminal';
      criminalCategory = legacyType;
    }
  }

  return {
    ...existingCase,
    ...(caseCategory && { caseCategory }),
    ...(criminalCategory && { criminalCategory }),
    caseType,
    ...(proceedingType && { proceedingType, matterType: proceedingType }),
    ...(caseSubtype && { caseSubtype }),
    legacyCaseType: legacyType,
  };
};

export function resolveLegacyCriminalProceeding(
  matterTypeId: string,
  caseSubtype?: string,
): { categoryId: string; proceedingId: string } | undefined {
  const subtypeKey = (caseSubtype || '').toLowerCase();
  if (subtypeKey && LEGACY_CRIMINAL_SUBTYPE_MAP[subtypeKey]) {
    return LEGACY_CRIMINAL_SUBTYPE_MAP[subtypeKey];
  }
  const direct = LEGACY_CRIMINAL_TYPE_MAP[matterTypeId] || LEGACY_CRIMINAL_TYPE_MAP[matterTypeId.toLowerCase()];
  if (direct) return direct;
  const proceeding = findCriminalProceedingById(CRIMINAL_CASE_TAXONOMY_CONFIG, matterTypeId);
  if (proceeding?.criminalCategoryId) {
    return { categoryId: proceeding.criminalCategoryId, proceedingId: proceeding.id };
  }
  return undefined;
}

export function getCriminalProceedingConfig(categoryId: string, proceedingId: string) {
  return getCriminalProceeding(CRIMINAL_CASE_TAXONOMY_CONFIG, categoryId, proceedingId);
}
